import * as React from "react"
import {
  Database,
  Book,
  UserCheck,
  BookDashed,
  LayoutDashboard,
  LogOut,
  Upload,
  Cat,
  ChevronRight,
} from "lucide-react"
import { Link, useLocation } from "react-router-dom"
import { Drawer } from "@mantine/core"
import {
  Sidebar,
  SidebarRail,
} from "../../../components/ui/sidebar"
import { AuthContext } from "../../../contexts/Auth/Auth.Context"
import { REACT_APP_SESSION_DATA } from "../../../constants"
import ExcelUploader from "../../../components/ExcelUploader"

/* ── Nav definition ────────────────────────────────────────────────── */
const MAIN_NAV = [
  { label: "User Management",    url: "/home/overview",            icon: UserCheck },
  { label: "Dataset Management", url: "/home/dataset-management",  icon: Database  },
  { label: "Articles & News",    url: "/home/articles",            icon: Book      },
]

const QUICK_NAV = [
  { label: "User Dashboard", url: "/dashboard",         icon: LayoutDashboard },
  { label: "Documentation",  url: "/home/documentation", icon: BookDashed      },
]

/* ── Sidebar component ─────────────────────────────────────────────── */
export function AppSidebar({ ...props }: React.ComponentProps<typeof Sidebar>) {
  const { state, dispatch } = React.useContext(AuthContext)
  const [opened, setOpened] = React.useState(false)
  const location = useLocation()

  const isActive = (url: string) => {
    const seg = url.split("/").pop() ?? ""
    return seg.length > 0 && location.pathname.includes(seg)
  }

  function signOut() {
    localStorage.removeItem(REACT_APP_SESSION_DATA)
    dispatch({ type: "SIGN_OUT" } as any)
    window.location.href = "/"
  }

  const name     = state.userData?.name     || state.userData?.username || "Admin"
  const username = state.userData?.username || ""
  const role     = state.userData?.role     || "Admin"
  const initials = name.slice(0, 2).toUpperCase()

  return (
    <>
      {/* ── Sidebar shell ────────────────────────────────────────── */}
      <Sidebar
        collapsible="icon"
        className="border-0 overflow-hidden"
        style={{
          background: "#041f0e",
          borderRight: "1px solid rgba(255,255,255,0.06)",
        }}
        {...props}
      >
        {/* ── Logo / brand ──────────────────────────────────────── */}
        <div
          className="flex items-center gap-3 px-3.5 py-4 shrink-0"
          style={{ borderBottom: "1px solid rgba(255,255,255,0.06)" }}
        >
          <div className="w-7 h-7 rounded-lg bg-green-900 flex items-center justify-center shrink-0 border border-green-700/40">
            <Cat size={13} className="text-green-300" />
          </div>
          <div className="overflow-hidden group-data-[collapsible=icon]:hidden">
            <p
              className="text-white text-xs font-extrabold tracking-widest leading-none"
              style={{ fontFamily: "var(--font-display, sans-serif)" }}
            >
              WITS
            </p>
            <p className="text-green-500 text-[9px] tracking-[0.2em] uppercase mt-0.5">
              Admin Console
            </p>
          </div>
        </div>

        {/* ── Upload button ─────────────────────────────────────── */}
        <div className="px-3 pt-3 pb-1 shrink-0 group-data-[collapsible=icon]:px-2">
          <button
            onClick={() => setOpened(true)}
            className="
              flex items-center gap-2 w-full px-3 py-2.5 rounded-xl
              text-xs font-semibold text-green-400
              border border-green-500/25
              hover:bg-green-500/10 hover:border-green-500/40
              transition-all duration-150
              group-data-[collapsible=icon]:justify-center
              group-data-[collapsible=icon]:px-0
              group-data-[collapsible=icon]:py-2.5
            "
            title="Upload Dataset"
          >
            <Upload size={14} className="shrink-0" />
            <span className="group-data-[collapsible=icon]:hidden">Upload Dataset</span>
          </button>
        </div>

        {/* ── Scrollable nav area ───────────────────────────────── */}
        <div
          className="flex-1 overflow-y-auto py-2 px-2 flex flex-col gap-0.5"
          style={{ scrollbarWidth: "none" }}
        >
          {/* Main section */}
          <p className="
            text-white/20 text-[9px] uppercase tracking-[0.15em] font-semibold
            px-2.5 pt-2 pb-1.5
            group-data-[collapsible=icon]:hidden
          ">
            Platform
          </p>

          {MAIN_NAV.map(({ label, url, icon: Icon }) => (
            <Link
              key={url}
              to={url}
              title={label}
              className={`
                flex items-center gap-3 px-2.5 py-2.5 rounded-xl transition-all duration-150 border
                group-data-[collapsible=icon]:justify-center
                group-data-[collapsible=icon]:px-0
                group-data-[collapsible=icon]:py-2.5
                ${isActive(url)
                  ? "bg-green-700/20 text-green-400 border-green-500/20"
                  : "text-white/40 hover:bg-white/5 hover:text-white/75 border-transparent"
                }
              `}
            >
              <Icon size={15} className="shrink-0" />
              <span className="text-xs font-medium truncate group-data-[collapsible=icon]:hidden">
                {label}
              </span>
              {isActive(url) && (
                <ChevronRight size={11} className="ml-auto shrink-0 text-green-500/60 group-data-[collapsible=icon]:hidden" />
              )}
            </Link>
          ))}

          {/* Divider */}
          <div
            className="my-2 mx-1"
            style={{ borderTop: "1px solid rgba(255,255,255,0.06)" }}
          />

          {/* Quick links section */}
          <p className="
            text-white/20 text-[9px] uppercase tracking-[0.15em] font-semibold
            px-2.5 pb-1.5
            group-data-[collapsible=icon]:hidden
          ">
            Quick Links
          </p>

          {QUICK_NAV.map(({ label, url, icon: Icon }) => (
            <Link
              key={url}
              to={url}
              title={label}
              className="
                flex items-center gap-3 px-2.5 py-2.5 rounded-xl
                text-white/30 hover:bg-white/5 hover:text-white/60
                transition-all duration-150 border border-transparent
                group-data-[collapsible=icon]:justify-center
                group-data-[collapsible=icon]:px-0
                group-data-[collapsible=icon]:py-2.5
              "
            >
              <Icon size={15} className="shrink-0" />
              <span className="text-xs truncate group-data-[collapsible=icon]:hidden">
                {label}
              </span>
            </Link>
          ))}
        </div>

        {/* ── Footer: user card + sign out ─────────────────────── */}
        <div
          className="p-2 shrink-0"
          style={{ borderTop: "1px solid rgba(255,255,255,0.06)" }}
        >
          {/* User card — hidden when collapsed */}
          <div
            className="
              px-2.5 py-2.5 rounded-xl mb-1.5
              group-data-[collapsible=icon]:hidden
            "
            style={{ background: "rgba(255,255,255,0.04)" }}
          >
            <div className="flex items-center gap-2.5 mb-2">
              <div className="w-7 h-7 rounded-full bg-green-800 border border-green-600/30 flex items-center justify-center text-white text-[11px] font-bold shrink-0">
                {initials}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-white/75 text-xs font-semibold truncate leading-tight">{name}</p>
                <p className="text-white/25 text-[10px] truncate">{username}</p>
              </div>
            </div>
            <span className="
              text-[9px] font-bold tracking-widest uppercase
              px-2 py-0.5 rounded-full border
              bg-amber-900/50 text-amber-400 border-amber-500/25
            ">
              {role}
            </span>
          </div>

          {/* Sign out */}
          <button
            onClick={signOut}
            title="Sign out"
            className="
              flex items-center gap-2 w-full px-2.5 py-2 rounded-xl
              text-white/25 hover:text-red-400 hover:bg-red-500/8
              transition-all duration-150
              group-data-[collapsible=icon]:justify-center
              group-data-[collapsible=icon]:px-0
            "
          >
            <LogOut size={14} className="shrink-0" />
            <span className="text-xs group-data-[collapsible=icon]:hidden">Sign out</span>
          </button>
        </div>

        <SidebarRail />
      </Sidebar>

      {/* ── Upload drawer ─────────────────────────────────────────── */}
      <Drawer
        opened={opened}
        onClose={() => setOpened(false)}
        position="right"
        title="Upload Dataset"
        padding="md"
        size="xl"
        withOverlay={false}
        zIndex={1100}
        styles={{
          drawer: {
            background: "#061a0e",
            borderLeft: "1px solid rgba(255,255,255,0.08)",
            display: "flex",
            flexDirection: "column",
          },
          header: {
            background: "#061a0e",
            borderBottom: "1px solid rgba(255,255,255,0.07)",
            paddingTop: 14,
            paddingBottom: 14,
          },
          title: {
            color: "rgba(255,255,255,0.85)",
            fontWeight: 700,
            fontSize: 14,
            letterSpacing: "0.02em",
          },
          closeButton: { color: "rgba(255,255,255,0.35)" },
          body: { flex: 1, display: "flex", flexDirection: "column", overflow: "auto" },
        }}
      >
        <ExcelUploader />
      </Drawer>
    </>
  )
}
